import React, { useMemo, useEffect } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry'

export function CurvedText({ text = "", bendFactor = 0.6, color="white", textHeight=0.2, ...props}) {
  // Load a font from an external URL.
  const font = useLoader(
    FontLoader,
    'https://raw.githubusercontent.com/mrdoob/three.js/refs/heads/dev/examples/fonts/helvetiker_regular.typeface.json'
  )

  // Use useMemo to create and memoize the TextGeometry.
  const geometry = useMemo(() => {
    const textGeometry = new TextGeometry(text, {
      font: font,
      size: 1,
      height: textHeight,
      curveSegments: 12,
      bevelEnabled: false,
    })
    textGeometry.center() // Center the geometry so bending occurs around its center
    return textGeometry
  }, [text, font, textHeight])

  // Apply the bending effect on the geometry vertices.
  useEffect(() => {
    geometry.computeBoundingBox()
    const positionAttribute = geometry.attributes.position
    const vertex = new THREE.Vector3()

    for (let i = 0; i < positionAttribute.count; i++) {
      vertex.fromBufferAttribute(positionAttribute, i)
      const t = vertex.x
      // Calculate the bending angle based on the vertex's x-position
      const angle = -(t - 0.5) * bendFactor
      const cos = Math.cos(angle)
      const sin = Math.sin(angle)

      // Rotate the vertex around the Y axis (affecting x and z)
      const x = vertex.x
      const z = vertex.z
      vertex.x = cos * x - sin * z
      vertex.z = sin * x + cos * z

      // Update the vertex position back in the geometry
      positionAttribute.setXYZ(i, vertex.x, vertex.y, vertex.z)
    }
    // Notify Three.js that the geometry has been modified.
    positionAttribute.needsUpdate = true
  }, [geometry, bendFactor])

  return (
    <mesh geometry={geometry} {...props}>
      <meshStandardMaterial color={color} />
    </mesh>
  )
}
